import { useEffect } from 'react';
import { trackPageView, trackEvent, trackButtonClick, trackFormSubmission, trackScrollDepth } from '../utils/analytics';

// Hook for tracking page views automatically
export const usePageTracking = () => {
  useEffect(() => {
    // Track initial page load
    trackPageView(window.location.pathname + window.location.search);
  }, []);
};

// Hook for tracking scroll depth
export const useScrollTracking = () => {
  useEffect(() => {
    let maxScroll = 0;
    const scrollThresholds = [25, 50, 75, 90, 100];
    const trackedThresholds = new Set();

    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = Math.round((scrollTop / scrollHeight) * 100);

      if (scrollPercent > maxScroll) {
        maxScroll = scrollPercent;
      }

      // Track scroll depth milestones
      scrollThresholds.forEach(threshold => {
        if (scrollPercent >= threshold && !trackedThresholds.has(threshold)) {
          trackedThresholds.add(threshold);
          trackScrollDepth(threshold);
        }
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
};

// Hook for tracking time on page
export const useTimeTracking = () => {
  useEffect(() => {
    const startTime = Date.now();

    const handleBeforeUnload = () => {
      const timeSpent = Math.round((Date.now() - startTime) / 1000);
      trackEvent('timing_complete', 'engagement', 'time_on_page', timeSpent);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);
};

// Custom hook for button click tracking
export const useButtonTracking = (buttonName, location = 'unknown') => {
  const handleClick = () => {
    trackButtonClick(buttonName, location);
  };

  return handleClick;
};

// Custom hook for form tracking
export const useFormTracking = (formName) => {
  const handleSubmit = () => {
    trackFormSubmission(formName);
  };

  return handleSubmit;
};

// Hook for tracking custom events
export const useEventTracking = () => {
  const trackCustomEvent = (action, category, label, value) => {
    trackEvent(action, category, label, value);
  };

  return trackCustomEvent;
};
